import { createContext, useContext } from "react";
import { usePanelRef } from "react-resizable-panels";
import type {
    TaskDetailPanelType,
    TaskDetailSectionRequest,
} from "./TaskDetail/constants";

export interface OpenTaskParams {
    taskId: string;
    /** The task's workspace, kept in the URL so the organization page can open it before its list loads. */
    workspaceId?: string;
}

/** The sidebar section to open, on the task whose sidebar should open it. */
export interface TaskDetailSectionTarget {
    section: TaskDetailSectionRequest;
    taskId: string;
}

export interface TasksContextValue {
    /** The open task, read from the URL's `taskId`. */
    selectedTaskId: string | null;
    /** The last open came from a key (Enter on a row or card), so the task view skips its fade. */
    isOpenedByKeyboard: boolean;
    /** Opens a task in a new history entry, so Back closes it. */
    openTask: (params: OpenTaskParams) => void;
    /** Swaps the open task in place (pager, next task). */
    goToTask: (params: OpenTaskParams) => void;
    closeTask: () => void;
    taskDetailPanelRef: ReturnType<typeof usePanelRef>;
    setTaskDetailPanelCollapsed: (collapsed: boolean) => void;
    setTaskDetailPanelContent: (content: TaskDetailPanelType) => void;
    /** Expands the pane on that tab, never collapsing it. */
    showTaskDetailPanel: (type: TaskDetailPanelType) => void;
    /** Shows a section of the right pane by selecting the section's tab. */
    requestTaskDetailSection: (target: TaskDetailSectionTarget) => void;
}

/**
 * The right pane's state, kept out of `TasksContext` so toggling the pane
 * doesn't re-render the task list hidden behind the open task.
 */
export interface TaskPaneState {
    taskDetailPanelCollapsed: boolean;
    /**
     * The right pane tab asked for. Paging keeps it; the pane shows it when
     * the task has that tab. Null, as after opening a task from the list,
     * means each task's default tab.
     */
    taskDetailPanelContent: TaskDetailPanelType | null;
}

export const TasksContext = createContext<TasksContextValue | undefined>(
    undefined,
);

/**
 * What list rows and board cards call from events. Unlike `TasksContext`,
 * its value doesn't change when a task opens or the pager moves, so those
 * don't re-render every row. `TasksContextProvider` derives it.
 */
export type TaskRowActions = Pick<
    TasksContextValue,
    | "taskDetailPanelRef"
    | "setTaskDetailPanelContent"
    | "requestTaskDetailSection"
> & {
    /** The open task when called. */
    getSelectedTaskId: () => string | null;
};

export const TaskRowActionsContext = createContext<TaskRowActions | null>(null);

export const useTaskRowActions = () => useContext(TaskRowActionsContext);

/** Whether a task is open. Keeps its value while paging between tasks. */
export const IsTaskOpenContext = createContext(false);

export const useIsTaskOpen = () => useContext(IsTaskOpenContext);

export const TaskPaneStateContext = createContext<TaskPaneState | undefined>(
    undefined,
);

export const useTaskPaneState = () => {
    const context = useContext(TaskPaneStateContext);
    if (!context) {
        throw new Error("useTaskPaneState must be used within TasksProvider");
    }
    return context;
};

export const useTasksContext = () => {
    const context = useContext(TasksContext);
    if (!context) {
        throw new Error("useTasksContext must be used within TasksProvider");
    }
    return context;
};

export const useTasksContextMaybe = (): TasksContextValue | null =>
    useContext(TasksContext) ?? null;
